// ============================================================================
// InstallPWAButton — botão "Adicionar à tela de início" pra mobile
// ============================================================================
// - Android/Chrome: captura `beforeinstallprompt` e dispara o prompt nativo
//   ao clicar. Esconde quando o evento não disparou (já instalado / desktop).
// - iOS Safari: não tem API de instalação. Mostra um modal com instruções
//   passo-a-passo (Compartilhar → Adicionar à Tela de Início).
// - Já instalado (display-mode: standalone OU navigator.standalone): retorna
//   null — não polui a tela.
// - Dispensa: salva flag em localStorage com TTL de 7 dias (volta a aparecer
//   depois caso o usuário mude de ideia).
//
// O evento `beforeinstallprompt` precisa ser capturado o quanto antes (ele
// dispara uma única vez por load). Por isso usamos um listener global em
// `main.jsx` que guarda o evento em `window.__deferredInstallPrompt`, e este
// componente só lê.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Smartphone, Share, Plus, X } from 'lucide-react';

const DISMISS_KEY = 'fk_install_dismissed_at';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 dias

function isStandalone() {
  if (typeof window === 'undefined') return false;
  // Android/Desktop instalado
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // iOS Safari adicionado à tela inicial
  if (window.navigator?.standalone === true) return true;
  return false;
}

function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) && !window.MSStream;
}

function isMobile() {
  if (typeof navigator === 'undefined') return false;
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function wasDismissedRecently() {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (!ts) return false;
    return (Date.now() - ts) < DISMISS_TTL_MS;
  } catch { return false; }
}

export default function InstallPWAButton() {
  const [deferredPrompt, setDeferredPrompt] = useState(() => window.__deferredInstallPrompt || null);
  const [installed, setInstalled] = useState(() => isStandalone());
  const [dismissed, setDismissed] = useState(() => wasDismissedRecently());
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    if (installed) return;
    const onPrompt = (e) => {
      e.preventDefault();
      window.__deferredInstallPrompt = e;
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      window.__deferredInstallPrompt = null;
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [installed]);

  // Não renderiza nada quando: já instalado, em desktop (não mobile),
  // dispensado recentemente, ou (em Android) o evento ainda não chegou
  // — ESSA última pra evitar mostrar botão que não funciona.
  if (installed) return null;
  if (!isMobile()) return null;
  if (dismissed) return null;
  // Android sem prompt disponível ainda → não mostra (evita confusão)
  if (!isIOS() && !deferredPrompt) return null;

  const handleAndroidInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setInstalled(true);
      }
    } catch {}
    setDeferredPrompt(null);
    window.__deferredInstallPrompt = null;
  };

  const handleIOSGuide = () => setShowIOSGuide(true);

  const handleDismiss = (e) => {
    e.stopPropagation();
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setDismissed(true);
  };

  const handleClick = () => {
    if (isIOS()) handleIOSGuide();
    else handleAndroidInstall();
  };

  return (
    <>
      <div className="relative mt-4">
        <button
          type="button"
          onClick={handleClick}
          className="w-full bg-[#16243A]/80 border border-brand-300/30 hover:border-brand-300/60 text-[#E9EEF5] rounded-xl px-4 py-3 text-sm font-medium flex items-center gap-3 transition-all hover:bg-[#1A2A42] active:scale-[0.99]"
        >
          <div className="w-8 h-8 rounded-lg bg-brand-300/15 border border-brand-300/20 flex items-center justify-center flex-shrink-0">
            <Smartphone size={15} className="text-brand-300" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-xs font-semibold leading-tight">Instalar na tela de início</p>
            <p className="text-[10px] text-[#8C9CB5] mt-0.5">Acesso rápido como um app</p>
          </div>
          <Plus size={16} className="text-brand-300/70 flex-shrink-0" />
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dispensar"
          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#0A1322] border border-[#23344F] text-[#5B6C86] hover:text-[#A3B1C6] flex items-center justify-center"
        >
          <X size={11} />
        </button>
      </div>

      {/* Modal de instruções iOS */}
      {showIOSGuide && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIOSGuide(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="bg-[#16243A] border border-[#23344F] rounded-2xl max-w-sm w-full p-6 shadow-2xl animate-slide-up"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-brand-300/15 border border-brand-300/20 flex items-center justify-center">
                  <Smartphone size={18} className="text-brand-300" />
                </div>
                <div>
                  <h3 className="text-[#E9EEF5] font-semibold">Adicionar à tela</h3>
                  <p className="text-[10px] text-[#8C9CB5]">No Safari iPhone/iPad</p>
                </div>
              </div>
              <button
                onClick={() => setShowIOSGuide(false)}
                className="text-[#5B6C86] hover:text-[#A3B1C6] p-1"
                aria-label="Fechar"
              >
                <X size={18} />
              </button>
            </div>

            <ol className="space-y-3.5 mt-2">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-brand-300/15 border border-brand-300/30 text-brand-300 text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
                <p className="text-[#E9EEF5] text-sm leading-snug">
                  Toque no ícone <Share size={14} className="inline -mt-0.5 mx-1 text-brand-300" /> <strong>Compartilhar</strong> na barra do Safari.
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-brand-300/15 border border-brand-300/30 text-brand-300 text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
                <p className="text-[#E9EEF5] text-sm leading-snug">
                  Role para baixo e toque em <strong>"Adicionar à Tela de Início"</strong>.
                </p>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-brand-300/15 border border-brand-300/30 text-brand-300 text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
                <p className="text-[#E9EEF5] text-sm leading-snug">
                  Confirme tocando em <strong>"Adicionar"</strong> no canto superior direito.
                </p>
              </li>
            </ol>

            <div className="mt-5 p-3 bg-[#0A1322] border border-[#23344F] rounded-xl">
              <p className="text-[11px] text-[#8C9CB5] leading-relaxed">
                💡 Pronto! Vai aparecer um ícone do <strong className="text-brand-300">Feel King</strong> na sua tela inicial — abre como um app, em tela cheia, sem barra do navegador.
              </p>
            </div>

            <button
              onClick={() => setShowIOSGuide(false)}
              className="w-full mt-4 bg-brand-300/15 hover:bg-brand-300/25 border border-brand-300/30 text-brand-300 font-semibold py-2.5 rounded-xl text-sm transition-colors"
            >
              Entendi
            </button>
          </div>
        </div>
      )}
    </>
  );
}
